import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ShippingPolicyDisplay from '@/components/ui-v2/shipping-policy/free-shipping-configurator';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useShippingPolicy } from '@/lib/hooks/useShippingPolicy';
import { trackEvent } from '@/lib/mixpanel';
import { ShippingPolicy } from '@/lib/types/shipping-policy';
import {
  formatMinimumOrderStartDate,
  offerDurationToDays,
} from '@/lib/types/shipping-policy';
import { cn, formatCurrency } from '@/lib/utils';
import { storeAtom } from '@/states/store.state';
import Button from '@appchoose/button';
import { useAtomValue } from 'jotai';
import { Clock, Edit } from 'lucide-react';

interface FreeShippingBlockProps {
  dealId: string;
  hubspotStartDate?: Date | null;
  disabled?: boolean;
}

function FreeShippingBlock({
  dealId,
  hubspotStartDate,
  disabled = false,
}: FreeShippingBlockProps) {
  const { t, i18n } = useTranslation();
  const store = useAtomValue(storeAtom);
  const isOfferLocked = useIsOfferLocked();
  const { shippingPolicy, handleShippingPolicyChange } =
    useShippingPolicy(dealId);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isPickupPointDeliveryFree =
    shippingPolicy.pickupPointEnabled &&
    shippingPolicy.pickupPointDeliveryPrice === 0;

  const isPolicyActive =
    shippingPolicy.minimumOrderEnabled ||
    shippingPolicy.timeLimitedEnabled ||
    shippingPolicy.allShippingFeesZero ||
    isPickupPointDeliveryFree;
  const bothOptionsEnabled =
    shippingPolicy.minimumOrderEnabled && shippingPolicy.timeLimitedEnabled;
  const isDisabled = disabled || shippingPolicy.allShippingFeesZero;
  const canConfigure = !isOfferLocked && !isDisabled;
  const hasMinimumOrderAmount =
    shippingPolicy.minimumOrderAmount !== null ||
    (shippingPolicy.pickupPointEnabled &&
      shippingPolicy.pickupPointDeliveryPrice !== 0 &&
      shippingPolicy.pickupPointMinimumOrderAmount !== null);
  const hasSplitMinimumOrderAmount =
    shippingPolicy.pickupPointEnabled &&
    shippingPolicy.minimumOrderSplitByDeliveryMode;
  const displaysDeliveryModeDetails =
    hasSplitMinimumOrderAmount || isPickupPointDeliveryFree;
  const homeMinimumLabel =
    shippingPolicy.minimumOrderEnabled &&
    shippingPolicy.minimumOrderAmount !== null
      ? displaysDeliveryModeDetails
        ? t(
            'ui-v2.settings.blocks-shipping-policy.free-shipping.home-minimum',
            {
              amount: formatCurrency(shippingPolicy.minimumOrderAmount, store),
            }
          )
        : formatCurrency(shippingPolicy.minimumOrderAmount, store)
      : null;
  const pickupPointMinimumLabel =
    shippingPolicy.minimumOrderEnabled &&
    hasSplitMinimumOrderAmount &&
    shippingPolicy.pickupPointDeliveryPrice !== 0 &&
    shippingPolicy.pickupPointMinimumOrderAmount !== null
      ? t(
          'ui-v2.settings.blocks-shipping-policy.free-shipping.pickup-point-minimum',
          {
            amount: formatCurrency(
              shippingPolicy.pickupPointMinimumOrderAmount,
              store
            ),
          }
        )
      : null;
  const showHomePaidSummary =
    displaysDeliveryModeDetails && shippingPolicy.minimumOrderAmount === null;
  const showPickupPointPaidSummary =
    hasSplitMinimumOrderAmount &&
    shippingPolicy.pickupPointDeliveryPrice !== 0 &&
    shippingPolicy.pickupPointMinimumOrderAmount === null;
  const showDeliveryPolicySummary =
    isPickupPointDeliveryFree ||
    showHomePaidSummary ||
    showPickupPointPaidSummary;

  const getEndDate = (): string | null => {
    if (!shippingPolicy.timeLimitedEnabled || !shippingPolicy.offerDuration) {
      return null;
    }

    const startDate = hubspotStartDate
      ? new Date(hubspotStartDate)
      : new Date();
    const days = offerDurationToDays(shippingPolicy.offerDuration);
    if (!days) return null;

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + days);

    return endDate.toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
    });
  };

  const endDate = getEndDate();
  const minimumOrderStartDate =
    bothOptionsEnabled && shippingPolicy.offerDuration
      ? formatMinimumOrderStartDate(
          hubspotStartDate,
          shippingPolicy.timeLimitedEnabled,
          shippingPolicy.offerDuration,
          i18n.language
        )
      : null;

  const openConfiguration = () => {
    if (!canConfigure) return;

    setConfigModalOpen(true);
    trackEvent('FREE_SHIPPING_CONFIGURATION_OPENED', { dealId });
  };

  return (
    <>
      <section
        onClick={openConfiguration}
        className={cn(
          'flex items-center justify-between gap-4 rounded-lg border border-neutral-200 p-6 transition-colors',
          isOfferLocked
            ? 'cursor-not-allowed opacity-50'
            : !isDisabled
              ? 'cursor-pointer hover:bg-neutral-50'
              : ''
        )}
      >
        <div className="flex-1">
          <div className="mb-4 flex items-center gap-3">
            <h2 className="text-lg font-semibold">
              {t('ui-v2.settings.blocks-shipping-policy.free-shipping.title')}
            </h2>
            <span
              className={cn(
                'rounded-full px-2.5 py-0.5 text-xs font-medium',
                isPolicyActive
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700'
              )}
            >
              {isPolicyActive
                ? t('ui-v2.settings.blocks-shipping-policy.status-active')
                : t('ui-v2.settings.blocks-shipping-policy.status-inactive')}
            </span>
          </div>

          {isPolicyActive && !shippingPolicy.allShippingFeesZero && (
            <div className="space-y-6">
              {/* Time-limited offer */}
              {shippingPolicy.timeLimitedEnabled &&
                shippingPolicy.offerDuration && (
                  <div className="space-y-2 border-l border-l-gray-300 pl-4">
                    <p className="text-sm text-muted-foreground">
                      {t(
                        'ui-v2.settings.blocks-shipping-policy.free-shipping.duration-label'
                      )}
                    </p>
                    <p className="text-xl font-semibold text-foreground">
                      {shippingPolicy.offerDuration}
                    </p>
                    {endDate && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        <span>
                          {t(
                            'ui-v2.settings.blocks-shipping-policy.free-shipping.ends-on-at',
                            { date: endDate }
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                )}

              {/* Minimum order amount */}
              {shippingPolicy.minimumOrderEnabled && hasMinimumOrderAmount && (
                <div
                  data-section="special-shipping-rule"
                  className="space-y-2 border-l border-l-gray-300 pl-4"
                >
                  <p className="text-sm text-muted-foreground">
                    {t('components.other.min_purchase_amount')}
                  </p>
                  <div className="space-y-1">
                    {homeMinimumLabel && (
                      <p className="text-xl font-semibold text-foreground">
                        {homeMinimumLabel}
                      </p>
                    )}
                    {pickupPointMinimumLabel && (
                      <p className="text-xl font-semibold text-foreground">
                        {pickupPointMinimumLabel}
                      </p>
                    )}
                  </div>
                  {minimumOrderStartDate && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                      <span>
                        {t(
                          'ui-v2.settings.blocks-shipping-policy.free-shipping.starts-on-at',
                          { date: minimumOrderStartDate }
                        )}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Underlying delivery policy, kept separate from special rules. */}
              {showDeliveryPolicySummary && (
                <div
                  data-section="shipping-policy-summary"
                  className="space-y-2 border-l border-l-gray-300 pl-4"
                >
                  {isPickupPointDeliveryFree && (
                    <p className="text-xl font-semibold text-foreground">
                      {t(
                        'ui-v2.settings.blocks-shipping-policy.free-shipping.pickup-point-offered-summary'
                      )}
                    </p>
                  )}
                  {showHomePaidSummary && (
                    <p className="text-sm text-muted-foreground">
                      {t(
                        'ui-v2.settings.blocks-shipping-policy.free-shipping.home-paid-summary'
                      )}
                    </p>
                  )}
                  {showPickupPointPaidSummary && (
                    <p className="text-sm text-muted-foreground">
                      {t(
                        'ui-v2.settings.blocks-shipping-policy.free-shipping.pickup-point-paid-summary'
                      )}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {!isPolicyActive && (
            <p className="text-sm text-muted-foreground">
              {t(
                'ui-v2.settings.blocks-shipping-policy.free-shipping.no-policy'
              )}
            </p>
          )}

          {shippingPolicy.allShippingFeesZero && (
            <p className="text-sm text-muted-foreground">
              {t(
                'ui-v2.settings.blocks-shipping-policy.free-shipping.no-conditions.description',
                { currency: formatCurrency(0, store) }
              )}
              {t(
                'ui-v2.settings.blocks-shipping-policy.free-shipping.no-conditions.to_disable'
              )}
            </p>
          )}
        </div>

        {!isDisabled && (
          <Button
            type="button"
            appearance="outline"
            containsIcon={true}
            onClick={(e) => {
              e.stopPropagation();
              openConfiguration();
            }}
            disabled={isOfferLocked}
            className="shrink-0 gap-2 border-0"
          >
            <Edit className="h-4 w-4" />
            {t('ui-v2.settings.blocks-shipping-policy.action-update')}
          </Button>
        )}
      </section>

      {configModalOpen && !isDisabled && (
        <ShippingPolicyDisplay
          policy={shippingPolicy}
          onPolicyChange={async (newPolicy: ShippingPolicy) => {
            setIsSubmitting(true);
            try {
              const didSave = await handleShippingPolicyChange(newPolicy);
              if (!didSave) return;

              setConfigModalOpen(false);
              trackEvent('FREE_SHIPPING_CONFIGURATION_SAVED', {
                dealId,
                minimumOrderEnabled: newPolicy.minimumOrderEnabled,
                minimumOrderSplitByDeliveryMode:
                  newPolicy.minimumOrderSplitByDeliveryMode,
                homeMinimumConfigured: newPolicy.minimumOrderAmount !== null,
                pickupPointMinimumConfigured:
                  newPolicy.pickupPointMinimumOrderAmount !== null,
                timeLimitedEnabled: newPolicy.timeLimitedEnabled,
              });
            } finally {
              setIsSubmitting(false);
            }
          }}
          isLocked={isOfferLocked}
          isSubmitting={isSubmitting}
          open
          onOpenChange={setConfigModalOpen}
          hubspotStartDate={hubspotStartDate}
        />
      )}
    </>
  );
}

export default FreeShippingBlock;
